import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateActionUpdateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateActionUpdateTRPCInput = {
  Id: string;
  ParentActionId: string;
  Title: string;
  Description: string;
  CustomAttributeData?: CustomAttributeData | null;
  OriginalTimestamp: string;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (): UpdateActionUpdateMutation => {
  return {
    update_action_update: {
      __typename: 'action_update_mutation_response',
      affected_rows: 1,
    },
  };
};

export const useUpdateActionUpdateTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.action.updates.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.action.updates.getActionUpdatesByParentActionId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.action.updates.getActionUpdateById.queryKey(),
        });
      },
    }),
  });

  return {
    updateActionUpdate: async (
      variables: UpdateActionUpdateTRPCInput
    ): Promise<UpdateActionUpdateMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
